import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import HeroesCarousel from "../../components/hero";
import LoadingHero from "../../components/hero/loadingHero";
import Academies from "../../components/carousel/academies";
import SFCarousels from "../../components/carousel";
import { getHeroes } from "../../services/heroService";
import { getInProgressCourses } from "../../services/courseService";
import {
  getUserJobRecommendedSkillPaths,
  getInProgressSkillPaths,
} from "../../services/skillPathService";
import { launchPadLayoutActions } from "../../redux/reducers/layoutReducer";
import { SFContentContainer } from "@sf-core";
import {
  selectDomainsByType,
  getAllDomains,
} from "../../redux/reducers/domainReducer";
import {
  getUserJobRoleCourses,
  selectUserJobCourses,
} from "../../redux/reducers/courseReducer";
import { profileActions } from "../../redux/reducers/profileReducer";
import { DOMAIN_TYPES } from "../../constants/domainTypes";
import { CAROUSEL_TYPES } from "../../constants/carouselTypes";
import { HERO_TYPES } from "../../constants/heroTypes";
import { skillPathPageActions } from "../../redux/reducers/skillPathPageReducer";

const Home = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [heroes, setHeroes] = useState(null);
  const academies = useSelector((state) =>
    selectDomainsByType(state, DOMAIN_TYPES.ACADEMY)
  );
  const userRelatedJobs = useSelector((state) =>
    selectDomainsByType(state, DOMAIN_TYPES.JOB)
  );
  const userJobsOfInterest = useSelector((state) =>
    selectDomainsByType(state, DOMAIN_TYPES.USER_JOB_GOAL)
  );
  const userSkillGoals = useSelector((state) =>
    selectDomainsByType(state, DOMAIN_TYPES.USER_SKILL_GOAL)
  );
  const userJobCourses = useSelector((state) => selectUserJobCourses(state));
  const userJobRole = useSelector((state) => state.userConfiguration.jobRole);
  const userJobRoleId = useSelector(
    (state) => state.userConfiguration.jobRoleId
  );
  const [resumeContents, setResumeContents] = useState([]);
  const [userJobRecommendedSkillPaths, setUserJobRecommendedSkillPaths] =
    useState([]);

  const lxpEnabledLearnerJobAndSkillVerification = useSelector(
    (state) =>
      state.session.tenantSettings.lxpEnabledLearnerJobAndSkillVerification
  );

  useEffect(() => {
    getHeroes().then((response) => {
      setHeroes(response.data);
      dispatch(launchPadLayoutActions.setState({ isSiteReady: true }));
      // Set the profile state to false after the user profile is saved
      dispatch(profileActions.setState({ saveUserProfileProgress: false }));
      // Set the active step of Profile Stepper based on the profile hero status
      // If the hero status is null, the active step is 0 else the active step is the hero status + 1
      let profileData = response.data.find(
        (item) => item.type === HERO_TYPES.Profile
      );
      let activeStep =
        typeof profileData?.status === "number" ? profileData?.status + 1 : 0;
      dispatch(
        profileActions.setState({
          activeStep: activeStep,
        })
      );
    });
    // Set the state of the skill path page to the default values
    dispatch(
      skillPathPageActions.setState({
        skillPathId: null,
        domainId: null,
        domainName: null,
        domainTypeId: null,
        domainStatus: null,
      })
    );
    dispatch(getAllDomains(DOMAIN_TYPES.ACADEMY));
    dispatch(getAllDomains(DOMAIN_TYPES.JOB));
    dispatch(getAllDomains(DOMAIN_TYPES.USER_JOB_GOAL));
    dispatch(getAllDomains(DOMAIN_TYPES.USER_SKILL_GOAL));
    dispatch(getUserJobRoleCourses());
    // Get the in progress courses
    getInProgressCourses().then((response) =>
      setResumeContents((prev) => [...prev, ...response.data])
    );
    // Get the in progress skill paths
    getInProgressSkillPaths().then((response) =>
      setResumeContents((prev) => [...prev, ...response.data])
    );
    // Get the user's job recommended skill paths
    getUserJobRecommendedSkillPaths().then((response) => {
      setUserJobRecommendedSkillPaths(response.data);
    });
  }, []);

  return (
    <SFContentContainer disableGutters hideGradient>
      {heroes ? <HeroesCarousel items={heroes} /> : <LoadingHero />}
      {academies.length !== 0 && <Academies items={academies} />}
      {resumeContents.length !== 0 && (
        <SFCarousels
          items={resumeContents}
          type={CAROUSEL_TYPES.ContinueLearning}
          title={t("homeScreen.continueLearning.title")}
        />
      )}
      {userJobCourses.length !== 0 && (
        <SFCarousels
          items={[...userJobRecommendedSkillPaths, ...userJobCourses]}
          type={CAROUSEL_TYPES.Courses}
          title={t("homeScreen.userRole.title")}
          userJobRole={{ title: userJobRole, id: userJobRoleId }}
        />
      )}

      {userSkillGoals.length !== 0 &&
        lxpEnabledLearnerJobAndSkillVerification && (
        <SFCarousels
          items={userSkillGoals}
          type={CAROUSEL_TYPES.SkillGoals}
          title={t("homeScreen.skillsGoals.title")}
        />
      )}

      {userJobsOfInterest.length !== 0 &&
        lxpEnabledLearnerJobAndSkillVerification && (
        <SFCarousels
          items={userJobsOfInterest}
          type={CAROUSEL_TYPES.JobOfInterest}
          title={t("homeScreen.jobOfInterest.title")}
        />
      )}

      {userRelatedJobs.length !== 0 && (
        <SFCarousels
          items={userRelatedJobs}
          type={CAROUSEL_TYPES.Jobs}
          title={t("homeScreen.relatedJobsTitle")}
        />
      )}
    </SFContentContainer>
  );
};

export default Home;
